import { useContext, useEffect } from "react";
import GenericDatePicker from "../../GenericDatePicker";
import GenericDatePickerReference from "../../GenericDatePickerReference";
import { ExportRelatorioContext } from "../ExportRelatorioContext";
import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup, TextField, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const handleInputChange = (filtro, value, setState) => {
  // Validação para datas inválidas
  if (filtro.variavel === "$F{dt_inicial}" || filtro.variavel === "$F{dt_final}" || filtro.variavel === "$F{dt_fim}") {
    // Se é uma data inválida, define como null
    if (value instanceof Date && isNaN(value.getTime())) {
      console.log(`=== Data inválida detectada para ${filtro.variavel} ===`);
      console.log('value:', value);
      value = null;
    }
  }

  setState((prevState) => {
    const itemExistente = prevState.find(
      (item) => item.variavel === filtro.variavel
    );

    if (itemExistente) {
      return prevState.map((item) =>
        item.variavel === filtro.variavel ? { ...item, valor: value } : item
      );
    } else {
      return [...prevState, { ...filtro, valor: value }];
    }
  });
};

const handleAutoCompleteChange = (filtro, value, setState) => {
  setState((prevState) => {
    const itemExistente = prevState.find(
      (item) => item.variavel === filtro.variavel
    );

    if (value.length > 0) {
      // Alterado para manter os valores como array puro
      const valores = value.map(v => v.detalhes);
      
      if (itemExistente) {
        return prevState.map((item) =>
          item.variavel === filtro.variavel
            ? { ...item, valor: valores, valores } // Mantém ambos os formatos
            : item
        );
      }
      return [...prevState, { ...filtro, valor: valores, valores }];
    }
    return prevState.filter(item => item.variavel !== filtro.variavel);
  });
};

export const ModoSaidaInput = ({ filtro }) => {
  const { valoresFiltros, setValoresFiltros } = useContext(ExportRelatorioContext);
  const valorAtual = valoresFiltros.find((item) => item.variavel === filtro.variavel)?.valor;
  const modo = (valorAtual && String(valorAtual).toUpperCase()) || "CRUZADAS";

  useEffect(() => {
    if (valorAtual === undefined || valorAtual === null || valorAtual === "") {
      setValoresFiltros((prev) => {
        const existe = prev.some((item) => item.variavel === filtro.variavel);
        if (existe) return prev.map((item) => item.variavel === filtro.variavel ? { ...item, valor: "CRUZADAS", valores: ["CRUZADAS"], parametros: ["$P{p_notas_saida}"] } : item);
        return [...prev, { ...filtro, valor: "CRUZADAS", valores: ["CRUZADAS"], parametros: ["$P{p_notas_saida}"] }];
      });
    }
  }, [filtro.variavel]);

  const handleChange = (_, value) => {
    const modoUpper = (value || "CRUZADAS").toUpperCase();
    setValoresFiltros((prev) => {
      const existe = prev.find((item) => item.variavel === filtro.variavel);
      if (existe) return prev.map((item) => item.variavel === filtro.variavel ? { ...item, valor: modoUpper, valores: [modoUpper], parametros: ["$P{p_notas_saida}"] } : item);
      return [...prev, { ...filtro, valor: modoUpper, valores: [modoUpper], parametros: ["$P{p_notas_saida}"] }];
    });
  };

  return (
    <FormControl
      component="fieldset"
      sx={{
        marginTop: "1rem",
        width: "100%",
        minWidth: 280,
        "& .MuiFormGroup-root": { flexWrap: "nowrap" },
      }}
    >
      <FormLabel component="legend" sx={{ mb: 1, display: "block" }}>
        Listar Notas de Saída
      </FormLabel>
      <RadioGroup row value={modo} onChange={handleChange} sx={{ gap: 2 }}>
        <FormControlLabel
          value="CRUZADAS"
          control={<Radio size="small" />}
          label={
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              Cruzadas
              <Tooltip title="Apenas cruzadas com Notas de Entrada" placement="top" arrow>
                <IconButton size="small" sx={{ p: 0.25 }} aria-label="Informação Cruzadas" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          }
          sx={{ mr: 2 }}
        />
        <FormControlLabel
          value="TODAS"
          control={<Radio size="small" />}
          label={
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              Todas
              <Tooltip title="Incluindo não cruzadas com Notas de Entrada" placement="top" arrow>
                <IconButton size="small" sx={{ p: 0.25 }} aria-label="Informação Todas" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
      </RadioGroup>
    </FormControl>
  );
};

export const DataInput = ({ filtro }) => {
  const { valoresFiltros, setValoresFiltros, nomeRelatorio } = useContext(
    ExportRelatorioContext
  );

  return (
    <div>
      <GenericDatePicker
        key={filtro.variavel}
        label={`Valor para ${filtro.nome}`}
        value={
          valoresFiltros.find((item) => item.variavel === filtro.variavel)
            ?.valor
        }
        onChange={(value) =>
          handleInputChange(filtro, value, setValoresFiltros)
        }
        sx={{ marginTop: "1rem", width: "100%" }}
      />
    </div>
  );
};

export const AutoCompleteInput = ({ filtro, combos }) => {
  const { valoresFiltros, setValoresFiltros, nomeRelatorio } = useContext(
    ExportRelatorioContext
  );

  const opcoesFormatadas = combos?.map(item => ({
    ...item,
    detalhes: item.detalhes.toString()
  })) || [];

  const getValorSelecionado = () => {
    const itemFiltro = valoresFiltros.find((item) => item.variavel === filtro.variavel);
    
    if (!itemFiltro || !itemFiltro.valor) return null;
    
    if (Array.isArray(itemFiltro.valor)) {
      return opcoesFormatadas.find(opcao => 
        itemFiltro.valor[0] === opcao.detalhes
      );
    }
    
    return opcoesFormatadas.find(opcao => 
      opcao.detalhes === itemFiltro.valor.toString()
    );
  };

   const handleChange = (_, value) => {
    setValoresFiltros(prevState => {
      if (!value) {
        return prevState.filter(item => item.variavel !== filtro.variavel);
      }

      // Armazena apenas o valor do ano como string
      return [
        ...prevState.filter(item => item.variavel !== filtro.variavel),
        { 
          ...filtro, 
          valor: value.detalhes,  // Apenas o valor do ano (string)
          valores: [value.detalhes], // Mantido como array para compatibilidade
          parametros: ["$P{p_ano}"] 
        }
      ];
    });
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Autocomplete
        options={opcoesFormatadas}
        disableCloseOnSelect={false}
        value={getValorSelecionado()}
        getOptionLabel={(option) => option.detalhes}
        isOptionEqualToValue={(option, value) => option.detalhes === value.detalhes}
        sx={{ marginTop: "1rem", width: "100%" }}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField {...params} label={`Valor para ${filtro.nome}`} />
        )}
        ListboxProps={{
          style: {
            maxHeight: '250px',
            overflow: 'auto'
          }
        }}
      />
    </div>
  );
};

export const TextFieldInput = ({ filtro }) => {
  const { valoresFiltros, setValoresFiltros, nomeRelatorio } = useContext(
    ExportRelatorioContext
  );

  console.log("Nome do relatório no TextFieldInput:", nomeRelatorio);

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}> {/* Corrigido o warning */}
      <TextField
        key={filtro.variavel}
        label={`Valor para ${filtro.nome}`}
        value={
          valoresFiltros.find((item) => item.variavel === filtro.variavel)
            ?.valor
        }
        onChange={(e) =>
          handleInputChange(filtro, e.target.value, setValoresFiltros)
        }
        sx={{ marginTop: "1rem", width: "100%" }}
      />
    </div>
  );


};

export const DataInputMesAno = ({ filtro }) => {
  const { valoresFiltros, setValoresFiltros } = useContext(
    ExportRelatorioContext
  );

  return (
    <div>
      <GenericDatePickerReference
        key={filtro.variavel}
        label={`Valor para ${filtro.nome}`}
        value={
          valoresFiltros.find((item) => item.variavel === filtro.variavel)
            ?.valor
        }
        onChange={(value) =>
          handleInputChange(filtro, value, setValoresFiltros)
        }
        sx={{ marginTop: "1rem", width: "100%" }}
      />
    </div>
  );
};
